import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { setShowPreloader } from "../../redux/action";
import UserBankAcountModel from "../../models/UserBankAcountModel";
import BankModel from "../../models/BankModel";
import AppNotification from "../../helpers/AppNotification";
import {
  RiBuilding4Line,
  RiUser3Line,
  RiPhoneLine,
  RiLockPasswordLine,
  RiKeyLine,
  RiCalendarLine,
  RiCloseLine,
  RiAddLine,
  RiVipCrown2Fill,
} from "react-icons/ri";

const createBankAccountSchema = Yup.object().shape({
  bank_id: Yup.string().required("Vui lòng chọn ngân hàng"),
  accountNumber: Yup.string()
    .required('Vui lòng nhập số tài khoản'),
  account_username: Yup.string()
    .max(255, "Tối đa 255 kí tự")
    .required("Trường bắt buộc")
    .test("uppercase-no-accent", "Tên chủ tài khoản phải viết hoa và không dấu", (value) => {
      if (!value) return true;
      const normalizedValue = value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();
      return normalizedValue === value;
    }),
  phone: Yup.string()
    .required('Vui lòng nhập số điện thoại')
    .test('phone-length', 'Số điện thoại phải có 10 chữ số', (value) => {
      if (value) {
        return value.length === 10;
      }
      return true;
    }),
  password_level_two: Yup.string()
    .required('Vui lòng nhập OTP')
    .test('password_level_two-length', 'Mã OTP phải có 6 chữ số', (value) => {
      if (value) {
        return String(value).length === 6;
      }
      return true;
    }),
  password: Yup.string()
    .min(6, 'Độ dài tối thiểu 6 ký tự')
    .max(32, 'Độ dài tối đa 32 ký tự')
    .required('Vui lòng nhập mật khẩu!'),
  registerTime: Yup.string()
    .required('Vui lòng chọn thời gian đăng ký')
});

function ModalCreateBankAccount(props) {
  const dispatch = useDispatch();
  const [banks, setBanks] = useState([]);

  const rawComboDays = props.userData
    ? (Number(props.userData.combo_days || 0) > 0
        ? Number(props.userData.combo_days)
        : Number(props.userData.combo_slots || 0) * 30)
    : 0;
  const comboSlots = Math.floor(rawComboDays / 30);
  const defaultVipTime =
    rawComboDays >= 30
      ? "combo_month"
      : rawComboDays >= 17
      ? "combo_half_month"
      : rawComboDays >= 9
      ? "combo_week"
      : "month";

  const [form, setForm] = useState({
    bank_id: props.bankId || "",
    accountNumber: "",
    account_username: "",
    phone: "",
    password: "",
    password_level_two: "",
    bin: props.bin || "",
    type: props.type || "",
    isPriority: props.isPriority || false,
    bank_name: "",
    registerTime: defaultVipTime
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      bank_id: props.bankId || prev.bank_id || "",
      bin: props.bin || prev.bin || "",
      type: props.type || prev.type || "",
      isPriority: props.isPriority || false,
      registerTime: defaultVipTime
    }));
  }, [props.bankId, props.bin, props.type, props.isPriority, defaultVipTime]);

  useEffect(() => {
    dispatch(setShowPreloader(true));
    BankModel.all()
      .then((res) => {
        dispatch(setShowPreloader(false));
        setBanks(res.data);
      })
      .catch((err) => {
        dispatch(setShowPreloader(false));
        console.log(err);
        AppNotification.showError("Lấy danh sách ngân hàng thất bại!");
      });
  }, [dispatch]);

  const normalizeText = (text) => {
    if (!text) return text;
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const getRegisterFee = (time) => {
     switch (time) {
      case "week":
        return "1.300.000";
      case "month":
        return "2.500.000";
      case "quarter":
        return "5.000.000";
      case "forever":
        return "10.000.000";
      default:
        return "0";
    }
  };

  const handleSubmit = async (values, { setFieldError }) => {
    const selectedBank = banks.find(bank => bank.id === values.bank_id);
    const submitValues = {
      ...values,
      bank_name: selectedBank ? selectedBank.name : "",
      bin: props.bin,
      type: props.type,
      isPriority: props.isPriority,
    };

    dispatch(setShowPreloader(true));
    UserBankAcountModel.createUserBankAcount(submitValues)
      .then((res) => {
        dispatch(setShowPreloader(false));
        if (res.success === true) {
          Swal.fire({
            icon: "success",
            title: res.message || "Tạo tài khoản ngân hàng thành công!",
            showConfirmButton: false,
            timer: 2200,
          });
          setTimeout(() => {
            if (props.reloadPage) {
              props.reloadPage();
            }
            props.onHide();
          }, 2000);
        } else {
          if (res.has_errors) {
            Object.keys(res.errors).forEach((fieldName) => {
              setFieldError(fieldName, res.errors[fieldName][0]);
            });
          } else {
            Swal.fire({
              icon: "error",
              title: res.data || "Tạo tài khoản thất bại!",
              showConfirmButton: false,
              timer: 2000,
            });
          }
        }
      })
      .catch((err) => {
        dispatch(setShowPreloader(false));
        Swal.fire({
          icon: "error",
          title: "Tạo tài khoản thất bại!",
          text: err.message || "Có lỗi xảy ra, vui lòng thử lại",
          showConfirmButton: false,
          timer: 2000,
        });
        console.log(err);
        AppNotification.showError("Tạo tài khoản thất bại!");
      });
  };

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <RiBuilding4Line className="me-2" style={{ color: 'var(--wus-text)' }} size={19} />
          Tạo Tài Khoản Bank Ảo
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={form}
          enableReinitialize={true}
          validationSchema={createBankAccountSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values }) => {
            const hasComboDays = rawComboDays > 0;
            const canUseCombo = rawComboDays >= 9;
            const isVipSelected = ["combo_month", "combo_half_month", "combo_week"].includes(values.registerTime);
            const getVipButtonText = (time) => {
              if (time === "combo_month") return "Tạo Bank VIP 1 Tháng (Trừ 30 ngày VIP - 0 VNĐ)";
              if (time === "combo_half_month") return "Tạo Bank VIP Nửa Tháng (Trừ 17 ngày VIP • Phí: 2 ngày)";
              if (time === "combo_week") return "Tạo Bank VIP 1 Tuần (Trừ 9 ngày VIP • Phí: 2 ngày)";
              return `Tạo Bank Ảo - ${getRegisterFee(time)} VNĐ`;
            };
            return (
            <Form className="p-2">
              {hasComboDays && (
                <div className="d-flex align-items-start gap-3 p-3 mb-3" style={{ background: 'var(--wus-warning-bg)', border: '1px solid var(--wus-warning-border)', borderRadius: '10px', color: 'var(--wus-text)' }}>
                  <RiVipCrown2Fill size={28} style={{ color: 'var(--wus-warning-text)', marginTop: '2px' }} className="flex-shrink-0" />
                  <div style={{ fontSize: '13px', width: '100%' }}>
                    <div style={{ fontWeight: '700', fontSize: '13.5px', color: 'var(--wus-warning-text)' }}>
                      QUỸ NGÀY VIP CỦA BẠN: {rawComboDays} NGÀY (~{comboSlots} LƯỢT{rawComboDays % 30 > 0 ? ` + ${rawComboDays % 30} NGÀY LẺ` : ''})
                    </div>
                    <div style={{ color: 'var(--wus-text-secondary)', marginTop: '4px', lineHeight: '1.5' }}>
                      Hỗ trợ tạo tài khoản VIP theo <strong>Tháng</strong>, <strong>Nửa tháng</strong> hoặc <strong>Tuần</strong> từ số ngày VIP còn dư (Miễn phí 100% phí sửa STK &amp; CK App):
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-2" style={{ fontSize: '12px' }}>
                      <span className="px-2 py-1 rounded" style={{ background: 'rgba(245, 158, 11, 0.14)', border: '1px solid rgba(245, 158, 11, 0.35)', color: 'var(--wus-warning-text)', fontWeight: 600 }}>
                        • 1 Tháng (30 ngày): <strong>30 ngày VIP</strong> (Không phí)
                      </span>
                      <span className="px-2 py-1 rounded" style={{ background: 'rgba(245, 158, 11, 0.14)', border: '1px solid rgba(245, 158, 11, 0.35)', color: 'var(--wus-warning-text)', fontWeight: 600 }}>
                        • Nửa Tháng (15 ngày): <strong>17 ngày VIP</strong> (Phí: 2 ngày)
                      </span>
                      <span className="px-2 py-1 rounded" style={{ background: 'rgba(245, 158, 11, 0.14)', border: '1px solid rgba(245, 158, 11, 0.35)', color: 'var(--wus-warning-text)', fontWeight: 600 }}>
                        • 1 Tuần (7 ngày): <strong>9 ngày VIP</strong> (Phí: 2 ngày)
                      </span>
                    </div>
                    {!canUseCombo && (
                      <div className="mt-2" style={{ color: 'var(--wus-danger-text)', fontSize: '12px', fontWeight: 600 }}>
                        ⚠️ Bạn còn {rawComboDays} ngày VIP lẻ (Cần tối thiểu 9 ngày VIP để tạo gói 1 Tuần gồm 7 ngày + Phí: 2 ngày).
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="mb-1" htmlFor="bank_id">
                      <RiBuilding4Line className="me-1" style={{ color: 'var(--wus-text-muted)' }} />
                      Chọn ngân hàng <span className="text-danger">*</span>
                    </label>
                    <Field
                      as="select"
                      name="bank_id"
                      className="form-control"
                    >
                      <option value="">-- Chọn ngân hàng --</option>
                      {banks.filter(bank => !bank.type.toLowerCase().includes('mb-event') && !bank.type.toLowerCase().includes('mb-pro-event')).sort((a, b) => a.type.toLowerCase().localeCompare(b.type.toLowerCase())).map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.type.replace('-', ' ')}
                        </option>
                      ))}
                    </Field>
                    {errors.bank_id && touched.bank_id ? (
                      <div className="text-danger small mt-1">
                        {errors.bank_id}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="mb-1" htmlFor="accountNumber">
                      <RiUser3Line className="me-1 text-primary" />
                      Số tài khoản <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="accountNumber"
                      className="form-control"
                      placeholder="Nhập số tài khoản ngân hàng thật"
                    />
                    {errors.accountNumber && touched.accountNumber ? (
                      <div className="text-danger small mt-1">
                        {errors.accountNumber}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="mb-1" htmlFor="account_username">
                      <RiUser3Line className="me-1 text-primary" />
                      Tên chủ tài khoản <span className="text-danger">*</span>
                    </label>
                    <Field name="account_username">
                      {({ field, form }) => (
                        <input
                          {...field}
                          type="text"
                          className="form-control"
                          placeholder="Tên sẽ được tự động tìm"
                          onChange={(e) => {
                            const normalizedValue = normalizeText(e.target.value);
                            form.setFieldValue(field.name, normalizedValue);
                          }}
                        />
                      )}
                    </Field>
                    {errors.account_username && touched.account_username ? (
                      <div className="text-danger small mt-1">
                        {errors.account_username}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="mb-1" htmlFor="phone">
                      <RiPhoneLine className="me-1 text-primary" />
                      Số điện thoại <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="phone"
                      className="form-control"
                      placeholder="Nhập số điện thoại đăng ký App"
                    />
                    {errors.phone && touched.phone ? (
                      <div className="text-danger small mt-1">{errors.phone}</div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="mb-1" htmlFor="password">
                      <RiKeyLine className="me-1 text-primary" />
                      Mật khẩu đăng nhập App <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="password"
                      name="password"
                      className="form-control"
                      placeholder="Tối thiểu 6 ký tự"
                    />
                    {errors.password && touched.password ? (
                      <div className="text-danger small mt-1">{errors.password}</div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="mb-1" htmlFor="password_level_two">
                      <RiLockPasswordLine className="me-1 text-primary" />
                      Mã OTP chuyển khoản <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="password_level_two"
                      className="form-control"
                      placeholder="6 chữ số"
                    />
                    {errors.password_level_two &&
                    touched.password_level_two ? (
                      <div className="text-danger small mt-1">
                        {errors.password_level_two}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="mb-1" htmlFor="registerTime">
                      <RiCalendarLine className="me-1" style={{ color: 'var(--wus-text-muted)' }} />
                      Thời gian sử dụng <span className="text-danger">*</span>
                    </label>
                    <Field
                      as="select"
                      name="registerTime"
                      className="form-control"
                      style={isVipSelected ? { borderColor: 'var(--wus-warning-border)', fontWeight: 600, color: 'var(--wus-warning-text)' } : undefined}
                    >
                      {rawComboDays >= 30 && (
                        <option value="combo_month">
                          👑 Gói VIP 1 Tháng (30 ngày) — Trừ 30 ngày VIP (Không phí • 0 VNĐ)
                        </option>
                      )}
                      {rawComboDays >= 17 && (
                        <option value="combo_half_month">
                          👑 Gói VIP Nửa Tháng (15 ngày) — Trừ 17 ngày VIP (Phí: 2 ngày • 0 VNĐ)
                        </option>
                      )}
                      {rawComboDays >= 9 && (
                        <option value="combo_week">
                          👑 Gói VIP 1 Tuần (7 ngày) — Trừ 9 ngày VIP (Phí: 2 ngày • 0 VNĐ)
                        </option>
                      )}
                      <option value="week">
                        Gói Thường 1 Tuần - {getRegisterFee("week")} VNĐ
                      </option>
                      <option value="month">
                        Gói Thường 1 Tháng - {getRegisterFee("month")} VNĐ
                      </option>
                    </Field>
                    {isVipSelected && (
                      <div className="mt-2 p-2 px-3 rounded d-flex align-items-center justify-content-between" style={{ background: 'var(--wus-warning-bg)', border: '1px dashed var(--wus-warning-border)', fontSize: '12.5px', color: 'var(--wus-warning-text)' }}>
                        <span>
                          {values.registerTime === "combo_month" && "✅ Tạo 1 Tháng (30 ngày): Trừ đúng 30 ngày VIP (1 lượt chẵn, không mất phí lẻ)."}
                          {values.registerTime === "combo_half_month" && "⚡ Tạo Nửa Tháng (15 ngày sử dụng): Chi phí = 17 ngày VIP (15 ngày + Phí: 2 ngày)."}
                          {values.registerTime === "combo_week" && "⚡ Tạo 1 Tuần (7 ngày sử dụng): Chi phí = 9 ngày VIP (7 ngày + Phí: 2 ngày)."}
                        </span>
                        <strong>
                          Còn lại: {rawComboDays - (values.registerTime === "combo_month" ? 30 : values.registerTime === "combo_half_month" ? 17 : 9)} ngày VIP
                        </strong>
                      </div>
                    )}
                    {errors.registerTime && touched.registerTime ? (
                      <div className="text-danger small mt-1">{errors.registerTime}</div>
                    ) : null}
                  </div>
                </div>
              </div>

              {!isVipSelected && (
                <div className="wus-info-box mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2 pb-2" style={{ borderBottom: '1px solid var(--wus-border)' }}>
                    <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--wus-text)' }}>
                      📋 Biểu phí dịch vụ tài khoản thường:
                    </span>
                    <span className="badge bg-info" style={{ fontSize: '11px' }}>Thu phí từng thao tác</span>
                  </div>
                  <div className="row g-2" style={{ fontSize: '12px', color: 'var(--wus-text-secondary)' }}>
                    <div className="col-6 d-flex align-items-center gap-1">
                      <span>• Check STK / CK App:</span> <strong style={{ color: 'var(--wus-danger-text)' }}>400đ / lần</strong>
                    </div>
                    <div className="col-6 d-flex align-items-center gap-1">
                      <span>• Push thông báo:</span> <strong style={{ color: 'var(--wus-danger-text)' }}>200đ / lần</strong>
                    </div>
                    <div className="col-6 d-flex align-items-center gap-1">
                      <span>• Sửa số dư hiển thị:</span> <strong style={{ color: 'var(--wus-danger-text)' }}>49.000đ / lần</strong>
                    </div>
                    <div className="col-6 d-flex align-items-center gap-1">
                      <span>• Sửa số tài khoản:</span> <strong style={{ color: 'var(--wus-danger-text)' }}>100.000đ / lần</strong>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 d-flex align-items-center gap-2" style={{ borderTop: '1px solid var(--wus-border)', fontSize: '11.5px', color: 'var(--wus-warning-text)' }}>
                    <RiVipCrown2Fill size={15} />
                    <span><strong>Mẹo tiết kiệm:</strong> Đăng ký <strong>Gói Combo VIP</strong> để được <strong>MIỄN PHÍ TOÀN BỘ</strong> các chi phí trên và bảo lưu lượt vĩnh viễn!</span>
                  </div>
                </div>
              )}

              <div className="d-flex gap-3 pt-2">
                <button 
                  type="button" 
                  className="btn btn-secondary flex-fill"
                  onClick={props.onHide}
                >
                  <RiCloseLine className="me-1" size={16} />
                  Hủy
                </button>
                {isVipSelected ? (
                  <button type="submit" className="btn btn-primary flex-fill">
                    <RiVipCrown2Fill className="me-1" size={16} />
                    {getVipButtonText(values.registerTime)}
                  </button>
                ) : (
                  <button type="submit" className="btn btn-primary flex-fill">
                    <RiAddLine className="me-1" size={16} />
                    Tạo Bank Ảo - {getRegisterFee(values.registerTime)} VNĐ
                  </button>
                )}
              </div>
            </Form>
          )}}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default ModalCreateBankAccount;
